// src/pages/home/News.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaMinus, FaPlus, FaTimes, FaUndo } from "react-icons/fa";

const fallbackNewsClippings = [
  {
    id: 1,
    src: "/images/news/clip-1.jpg",
    alt: "News clipping about Para Sports Association achievements",
    paper: "Local Daily",
    newsDate: "Date not available",
  },
  {
    id: 2,
    src: "/images/news/clip-2.jpg",
    alt: "News clipping about para-athlete Rongali Ravi",
    paper: "Regional Times",
    newsDate: "Date not available",
  },
  {
    id: 3,
    src: "/images/news/clip-3.jpg",
    alt: "News clipping covering a para-sports event",
    paper: "Sports Chronicle",
    newsDate: "Date not available",
  },
  {
    id: 4,
    src: "/images/news/clip-4.jpg",
    alt: "Article about Para Sports Association journey",
    paper: "State News",
    newsDate: "Date not available",
  },
  {
    id: 5,
    src: "/images/news/clip-5.jpg",
    alt: "Media coverage of medal winners",
    paper: "National Desk",
    newsDate: "Date not available",
  },
  {
    id: 6,
    src: "/images/news/clip-6.jpg",
    alt: "Newspaper article on para-sports",
    paper: "Daily Press",
    newsDate: "Date not available",
  },
];

const parseImagePathsInput = (rawValue) => {
  if (!rawValue) return [];

  if (Array.isArray(rawValue)) {
    return rawValue.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (typeof rawValue !== "string") return [];
  const raw = rawValue.trim();
  if (!raw) return [];

  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
    } catch (_err) {
      // Ignore JSON parse issue and continue with text splits.
    }
  }

  if (raw.includes("\n")) {
    return raw.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
  }
  if (raw.includes("|")) {
    return raw.split("|").map((item) => item.trim()).filter(Boolean);
  }
  if (raw.includes(",")) {
    return raw.split(",").map((item) => item.trim()).filter(Boolean);
  }

  return [raw];
};

const formatNewsDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const isDateLike = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  return !Number.isNaN(new Date(raw).getTime());
};

const News = () => {
  const [sections, setSections] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.25;

  useEffect(() => {
    let mounted = true;
    const loadNewsSections = async () => {
      try {
        const data = await API.fetchAboutSections("news");
        if (!mounted) return;
        setSections(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!mounted) return;
        setSections([]);
      }
    };
    loadNewsSections();
    return () => {
      mounted = false;
    };
  }, []);

  const managedNewsClippings = useMemo(() => {
    const rows = Array.isArray(sections)
      ? sections
          .filter((item) => item?.is_active !== false)
          .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0))
      : [];

    let clipCount = 0;
    const clips = [];

    rows.forEach((section) => {
      const sectionImages = [
        ...(section?.image_path ? [section.image_path] : []),
        ...parseImagePathsInput(section?.image_paths),
      ];
      const uniqueImages = [...new Set(sectionImages.filter(Boolean))];
      const heading = String(section?.heading || "").trim();
      const subheading = String(section?.subheading || "").trim();
      const subheadingDate = isDateLike(subheading) ? formatNewsDate(subheading) : "";
      const createdDate = formatNewsDate(section?.created_at || section?.updated_at);
      const paperName =
        heading || (subheadingDate ? "News Paper" : subheading) || "News Paper";
      const newsDate = subheadingDate || createdDate || "Date not available";

      uniqueImages.forEach((path, imageIndex) => {
        clipCount += 1;
        const fallbackIndex = (clipCount - 1) % fallbackNewsClippings.length;
        const fallbackClip = fallbackNewsClippings[fallbackIndex];
        clips.push({
          id: `${section.id || section.section_key || "news"}-${imageIndex}`,
          src: resolveAssetUrl(path, {
            folder: "page-sections",
            defaultPath: fallbackClip.src,
          }),
          alt: section?.subheading || section?.heading || `News clipping ${clipCount}`,
          paper: paperName || fallbackClip.paper,
          newsDate,
        });
      });
    });

    return clips;
  }, [sections]);

  const newsClippings = managedNewsClippings.length
    ? managedNewsClippings
    : fallbackNewsClippings;

  const openImage = (img) => {
    setSelectedImage(img);
    setZoom(1);
  };
  const handleClipClick = (event, clip) => {
    event.preventDefault();
    event.stopPropagation();
    openImage(clip);
  };
  const closeImage = () => {
    setSelectedImage(null);
    setZoom(1);
  };
  const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  const increaseZoom = () => setZoom((prev) => clampZoom(prev + ZOOM_STEP));
  const decreaseZoom = () => setZoom((prev) => clampZoom(prev - ZOOM_STEP));
  const resetZoom = () => setZoom(1);

  return (
    <>
      <Navbar />

      <main className="bg-slate-50">
        {/* ===== HERO SECTION ===== */}
        <section className="bg-white py-16 text-center border-b border-slate-200">
          <div className="max-w-screen-lg mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">In the Media</h1>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
              See how the journey and achievements of our athletes are being covered in the news.
            </p>
          </div>
        </section>

        {/* ===== NEWS CLIPS GRID ===== */}
        <section className="py-20 px-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
              {newsClippings.map((clip) => (
                <button
                  key={clip.id}
                  type="button"
                  onClick={(event) => handleClipClick(event, clip)}
                  className="relative block w-full text-left bg-white rounded-xl shadow-md overflow-hidden group transform transition-all duration-300 ease-in-out hover:shadow-xl"
                >
                  <img
                    src={clip.src}
                    alt={clip.alt}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                  <div className="p-3 border-t border-slate-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-700 truncate">
                      {clip.paper || "News Paper"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {clip.newsDate || "Date not available"}
                    </p>
                    <span className="inline-block mt-2 text-blue-600 text-sm font-semibold group-hover:underline">
                      View Full Clipping
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      {/* ===== LIGHTBOX MODAL ===== */}
      {selectedImage && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closeImage}
        >
          <button
            type="button"
            onClick={closeImage}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={decreaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom out"
            >
              <FaMinus size={14} />
            </button>
            <button
              type="button"
              onClick={increaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom in"
            >
              <FaPlus size={14} />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Reset zoom"
            >
              <FaUndo size={14} />
            </button>
          </div>

          <div
            className="relative max-w-5xl w-full max-h-[92vh] p-2 sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black/80">
              <div
                className="flex items-center justify-center p-2 sm:p-3 bg-black overflow-auto"
                onWheel={(event) => {
                  event.preventDefault();
                  if (event.deltaY < 0) {
                    increaseZoom();
                  } else {
                    decreaseZoom();
                  }
                }}
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                  style={{
                    transform: `scale(${zoom})`,
                    transition: "transform 120ms ease-out",
                  }}
                />
              </div>
              <div className="px-4 py-3 bg-black/85 text-white">
                <p className="text-sm font-semibold">
                  {selectedImage.paper || "News Paper"}
                </p>
                <p className="text-xs opacity-90">
                  {selectedImage.newsDate || "Date not available"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default News;
